import { baseApi } from 'api/baseApi';
import { BatchEditOperation } from 'types/batchEditObject.type';
import { TObjectJob, TObjectJobSummary } from 'types/objectJob.type';
import type { IAPIResult } from 'types/state.interface';
import { invalidateTagsByResponseId } from '../utils/jobUtil';

type TObjectJobResult = IAPIResult<TObjectJobSummary>;
type FetchObjectJobArgs = {
  page: number;
  limit: number;
  sortBy: string;
};

export type StartJobParams = {
  operationType: string;
  filter: Record<string, unknown>;
  operations: BatchEditOperation[];
};

export type StartDeleteJobParams = {
  filter: Record<string, unknown>;
};

export type ReuseJobParams = {
  operationType: string;
  jobId: string;
  operations: BatchEditOperation[];
};

type JobQueueStatus = {
  lastUpdatedJob?: { id: string; at: string };
  lastFinishedMutationJob?: { id: string; at: string };
  running: number;
  waiting: number;
};

export const objectJobApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    fetchObjectJobList: build.query<TObjectJobResult, FetchObjectJobArgs>({
      query: ({ page, limit, sortBy }) => ({
        url: '/objects/batch-operations/jobs',
        params: { page, limit, sortBy },
      }),
      providesTags: (result) => {
        const listTag = { type: 'ObjectJob' as const, id: 'LIST' };

        if (!result?.results?.length) {
          return [listTag];
        }

        return [
          listTag,
          ...result.results.map((job) => ({
            type: 'ObjectJob' as const,
            id: job.id,
          })),
        ];
      },
    }),
    fetchBatchEditObjectJob: build.query<TObjectJob, { id: string }>({
      query: ({ id }) => `/objects/batch-operations/jobs/${id}`,
      providesTags: (_, __, { id }) => [{ type: 'ObjectJob', id }],
    }),
    fetchJobQueueStatus: build.query<JobQueueStatus, void>({
      query: () => '/objects/batch-operations/jobs/status',
      providesTags: () => [{ type: 'ObjectJob', id: 'STATUS' }],
      onQueryStarted: async (_arg, api) => {
        // Invalidate and refresh list if in-progress jobs change

        const entry = api.getCacheEntry();
        if (!entry.data) return;
        const oldUpdated = entry.data.lastUpdatedJob?.at;

        try {
          const result = await api.queryFulfilled;

          const newUpdated = result.data.lastUpdatedJob?.at;
          if (oldUpdated !== newUpdated) {
            api.dispatch(
              objectJobApi.util.invalidateTags([
                { type: 'ObjectJob', id: 'LIST' },
              ])
            );
          }
        } catch {
          return;
        }
      },
    }),
    startJob: build.mutation<{ id: string }, StartJobParams>({
      query: (params) => ({
        method: 'POST',
        url: '/objects/batch-operations/jobs/batch-edit',
        body: params,
      }),
      invalidatesTags: invalidateTagsByResponseId,
    }),
    reuseJob: build.mutation<{ id: string }, ReuseJobParams>({
      query: ({ jobId, ...params }) => ({
        method: 'POST',
        url: `/objects/batch-operations/jobs/batch-edit/${jobId}/reuse`,
        body: params,
      }),
      invalidatesTags: invalidateTagsByResponseId,
    }),
    startDeleteJob: build.mutation<{ id: string }, StartDeleteJobParams>({
      query: (params) => ({
        method: 'POST',
        url: '/objects/batch-operations/jobs/batch-delete',
        body: params,
      }),
      invalidatesTags: invalidateTagsByResponseId,
    }),
    deleteJob: build.mutation<{ id: string }, { jobId: string }>({
      query: ({ jobId }) => ({
        method: 'DELETE',
        url: `/objects/batch-operations/jobs/${jobId}`,
      }),
      invalidatesTags: invalidateTagsByResponseId,
    }),
  }),
});
export const {
  useFetchObjectJobListQuery,
  useFetchBatchEditObjectJobQuery,
  useFetchJobQueueStatusQuery,
  useStartJobMutation,
  useStartDeleteJobMutation,
  useReuseJobMutation,
  useDeleteJobMutation,
} = objectJobApi;
